import {
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  List,
  ListItem,
  Show,
  Skeleton,
  SkeletonText,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { listingsModel } from "models/listings";
import type {
  APIListingApplicantFeedbackEntry,
  APIListingEvent,
} from "models/types/api-listing";
import { useNavigate, useParams } from "react-router-dom";
import BackArrowIcon from "assets/icons/medium/back-arrow.svg?react";
import EmptyRecord from "assets/icons/medium/empty-record.svg?react";
import { Empty } from "components/empty";
import { ScreenWrapper } from "components/screen-wrapper";
import { useIsAgencyFeaturePreferenceEnabled } from "hooks/use-is-agency-feature-preference-enabled";
import { SkeletonList } from "modules/vendor-portal/components/skeleton-list";
import { useWhichWordFactory } from "modules/vendor-portal/hooks/use-which-words";
import {
  createOpenHomeStats,
  StatsGrid,
} from "modules/vendor-portal/screens/open-home-details/components/open-home-details-stats-grid";
import { formatDuration } from "modules/vendor-portal/screens/open-home-details/utils";
import { FeedbackListItem } from "modules/vendor-portal/screens/overview/components/feedback/feedback-list-item";
import { formatEventDateTimeTitle } from "modules/vendor-portal/screens/overview/components/open-home-events/event-card";
import { generateSingularLabel } from "utils/generate-singular-label";

const getAttendeeCountForInterestLevel = (
  applicantFeedback: APIListingApplicantFeedbackEntry[],
  interestLevel: string,
) => {
  return applicantFeedback.filter((f) => f.interest_level?.id === interestLevel)
    .length;
};

export function OpenHomeDetailsScreen() {
  const { propertyId, eventId } = useParams<{
    propertyId: string;
    eventId: string;
  }>();
  const { isLoading, data } = useQuery(listingsModel.queries.read(propertyId));

  const navigate = useNavigate();

  const ww = useWhichWordFactory();

  const isAgencyFeaturePreferenceEnabled =
    useIsAgencyFeaturePreferenceEnabled();

  // Find the specific event based on eventId
  const event = data?.events?.find((event) => event.id === eventId) as
    | APIListingEvent
    | undefined;

  if (!data || !event) return null;

  const applicantFeedback =
    data.applicant_feedback?.entries.filter(
      (f) => f.parent_feedback.id === event.feedback?.id,
    ) || [];
  const attendeeCount = event.attendee_count || 0;
  const repeatAttendees = applicantFeedback
    .map((f) =>
      Math.max(
        ...(f.attendees ?? []).map((a) => a.times_inspected_running_total),
        0,
      ),
    )
    .filter((timesInspected) => timesInspected > 1).length;
  const hotAttendees = getAttendeeCountForInterestLevel(
    applicantFeedback,
    "hot",
  );
  const warmAttendees = getAttendeeCountForInterestLevel(
    applicantFeedback,
    "warm",
  );
  const coldAttendees = getAttendeeCountForInterestLevel(
    applicantFeedback,
    "cold",
  );

  // Parse event times and calculate duration
  const start = event.starts_at ? dayjs(event.starts_at) : null;
  const finish = event.ends_at ? dayjs(event.ends_at) : null;
  const durationObject =
    start && finish ? dayjs.duration(finish.diff(start)) : null;
  const duration = durationObject ? formatDuration(durationObject) : "";

  // Calculate average price indication
  const priceIndications = applicantFeedback
    .map((f) => f.price_indication)
    .filter((price) => price !== null && price !== undefined) as number[];
  let avgPriceIndication = "$0";
  if (priceIndications.length > 0) {
    const sum = priceIndications.reduce((acc, val) => acc + val, 0);
    const avg = Math.round(sum / priceIndications.length);
    avgPriceIndication = `$${
      avg >= 1000000
        ? (avg / 1000000).toFixed(1) + "m"
        : (avg / 1000).toFixed(0) + "k"
    }`;
  }

  // Create stats using the utility function from the new component
  const stats = createOpenHomeStats({
    attendeeCount,
    repeatAttendees,
    avgPriceIndication,
    hotAttendees,
    warmAttendees,
    coldAttendees,
    showInterestLevel: isAgencyFeaturePreferenceEnabled(
      "vendor_portal.functionality.interest_level.show_interest_level_data",
    ),
  });

  // Render summary text
  const renderSummary = (
    withHeading = true,
    marginTop = 0,
    marginBottom = 0,
  ) => (
    <>
      {withHeading && (
        <Text fontSize={"xl"} fontWeight={600} color={"gray.700"}>
          Summary
        </Text>
      )}
      <SkeletonText
        isLoaded={!isLoading}
        noOfLines={3}
        skeletonHeight={3}
        spacing={3}
        mt={marginTop}
        mb={marginBottom}
      >
        <Text fontSize={"lg"} color={"gray.600"}>
          {event.feedback?.note ||
            (data.address?.full_address
              ? `${generateSingularLabel(ww("events"), 1)} at ${
                  data.address.full_address
                }`
              : "No summary available.")}
        </Text>
      </SkeletonText>
    </>
  );

  // Render feedback list
  const renderApplicantFeedback = (withHeading = true, variant?: string) => {
    const hasFeedback = applicantFeedback.length > 0;

    return (
      <>
        {withHeading && (
          <Text fontSize={"xl"} fontWeight={600} color={"gray.700"}>
            Feedback
          </Text>
        )}
        <SkeletonList isLoaded={!isLoading} listSize={hasFeedback ? 1 : 0}>
          <List gap={3}>
            {applicantFeedback.map((applicantFeedbackEntry) => (
              <ListItem key={applicantFeedbackEntry.id} p={0} pb={3}>
                <Card py={4} px={3} variant={variant}>
                  <FeedbackListItem feedback={applicantFeedbackEntry} />
                </Card>
              </ListItem>
            ))}
            {!hasFeedback && (
              <Empty
                icon={<Icon as={EmptyRecord} boxSize={"auto"} />}
                emptyMessage={"Hang tight! Feedback is on the way"}
                emptyDesc={
                  "This section will be updated once feedback has been collected"
                }
              />
            )}
          </List>
        </SkeletonList>
      </>
    );
  };

  // Render desktop layout
  const renderDesktopLayout = () => (
    <Flex direction={"column"} gap={8}>
      <StatsGrid stats={stats} isLoading={isLoading} />
      {renderSummary()}
      <Flex direction={"column"} gap={6}>
        {renderApplicantFeedback(true)}
      </Flex>
    </Flex>
  );

  // Render mobile layout with tabs
  const renderMobileLayout = () => (
    <Tabs variant={"soft-rounded"}>
      <TabList overflowX={"scroll"}>
        <Tab whiteSpace={"nowrap"}>Summary</Tab>
        <Tab whiteSpace={"nowrap"}>Attendee feedback</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          {renderSummary(false, 5, 8)}
          <StatsGrid stats={stats} isLoading={isLoading} />
        </TabPanel>
        <TabPanel>{renderApplicantFeedback(false, "unstyled")}</TabPanel>
      </TabPanels>
    </Tabs>
  );

  // Format event title based on the event type and date
  const getEventTitle = () => {
    // Create a compatible object for formatEventDateTimeTitle
    const formattedDate = dayjs(event.starts_at).format("YYYY-MM-DD");
    const startTime = dayjs(event.starts_at).format("HH:mm:ss");
    const endTime = dayjs(event.ends_at).format("HH:mm:ss");

    return `${event.type.label} on ${formatEventDateTimeTitle({
      date_of: formattedDate,
      date_time_start: startTime,
      date_time_finish: endTime,
    })}`;
  };

  return (
    <ScreenWrapper flexProps={{ gap: 0 }}>
      <VStack gap={3} mb={8} alignItems={"flex-start"}>
        <HStack gap={2}>
          <Show above={"sm"}>
            <IconButton
              aria-label={"Back"}
              variant={"ghost"}
              icon={<Icon as={BackArrowIcon} boxSize={"16px"} />}
              color={"#000"}
              cursor={"pointer"}
              onClick={() => navigate(-1)}
            />
          </Show>
          <Skeleton isLoaded={!isLoading}>
            <Heading
              fontSize={{ base: "4xl", md: "2xl" }}
              fontWeight={{ base: 800, md: 700 }}
              as={"h1"}
            >
              {getEventTitle()}
            </Heading>
          </Skeleton>
        </HStack>
        {event.feedback?.agent?.name ? (
          <Show above={"sm"}>
            <Skeleton isLoaded={!isLoading}>
              <Text
                fontSize={"md"}
                color={"gray.600"}
              >{`${duration} • Hosted by ${event.feedback.agent.name}`}</Text>
            </Skeleton>
          </Show>
        ) : null}
      </VStack>

      <Show above={"sm"}>{renderDesktopLayout()}</Show>

      <Show below={"sm"}>{renderMobileLayout()}</Show>
    </ScreenWrapper>
  );
}
